import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import { useStore, type Reservation } from '../store/useStore'
import { mapApiReservation } from './reservationMap'

const ACTIVE_STATUSES: Reservation['status'][] = ['pending', 'approved']

/** JST の今日 (YYYY-MM-DD)。予約の date は店の営業日として JST で入っている。 */
function todayJst(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

/**
 * #1373 まだ「進行中」と呼べるか。
 *
 * 以前は status だけで分けており、2 つ壊れていた:
 *
 * 1. **日付が過ぎても進行中に残った** — 昨日の予約に「キャンセル期限を過ぎている
 *    ため店舗へ直接ご連絡ください」と出続ける。もう終わった予約に案内が出るのは
 *    お客様に失礼で、かつ何をすればいいのか分からない。
 * 2. **visited / no_show がどちらのリストにも入らず消えた** — 来店した予約が
 *    履歴から丸ごと消えていた。
 *
 * 日付で切る理由: 店主が「来店済」を押し忘れているだけの場合があり、
 * ボタンの有無で客の画面を変えると、店の操作漏れが客に見えてしまう。
 * 来店の有無に関わらず、予約日が過ぎたら履歴へ送る。
 *
 * 当日中は進行中のままにする (時刻で切ると、食事中の予約が消える)。
 */
function isOngoing(r: Reservation): boolean {
  return ACTIVE_STATUSES.includes(r.status) && r.date >= todayJst()
}

// #1024 (F1b): reservations の Firestore 公開直読は PII / 決済ID の全件漏洩に
// つながるため廃止。onSnapshot のリアルタイム購読を API ポーリングに置換した。
// 予約は秒単位性が不要なので 15s 間隔 + 画面復帰時に即時再取得で十分。
const POLL_INTERVAL_MS = 15_000

/**
 * 現在ログイン中ユーザーの予約一覧。
 * かつては onSnapshot でリアルタイム購読していたが、#1024 で API 経由に一本化。
 * 承認/却下/キャンセルの反映は最大 POLL_INTERVAL_MS 遅延する（画面復帰時は即時）。
 * 未ログイン時は空配列を返す。
 */
export function useMyReservations() {
  const { user } = useStore()
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(!!user?.id)

  // Reset state when the signed-in user changes (derive-state-from-props).
  const [trackedUserId, setTrackedUserId] = useState(user?.id)
  if (user?.id !== trackedUserId) {
    setTrackedUserId(user?.id)
    setReservations([])
    setLoading(!!user?.id)
  }

  // 進行中フェッチのレースを避けるため世代トークンで最新のみ採用。
  const genRef = useRef(0)

  const fetchOnce = useCallback(async () => {
    if (!user?.id) return
    const gen = ++genRef.current
    try {
      const res = await api.getMyReservations()
      if (gen !== genRef.current) return
      const list: Reservation[] = (res?.reservations ?? []).map(mapApiReservation)
      setReservations(list)
    } catch (err) {
      if (gen !== genRef.current) return
      console.error('[useMyReservations] fetch error:', err)
    } finally {
      if (gen === genRef.current) setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    fetchOnce()
    const id = window.setInterval(fetchOnce, POLL_INTERVAL_MS)
    const onFocus = () => {
      if (document.visibilityState === 'visible') fetchOnce()
    }
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [user?.id, fetchOnce])

  const toMs = (r: Reservation) => new Date(`${r.date}T${r.time}:00+09:00`).getTime()

  // #1218 (管理番号_017): 直近の予約から確認できるよう、予約日時の昇順で表示する。
  const active = reservations.filter(isOngoing).sort((a, b) => toMs(a) - toMs(b))
  // #1373 履歴は進行中の補集合にする。列挙式だと status が増えたときに
  // どちらにも入らず**画面から消える** (visited / no_show が実際にそうなっていた)。
  // 直近の出来事から見たいので降順。
  const history = reservations.filter((r) => !isOngoing(r)).sort((a, b) => toMs(b) - toMs(a))

  return { reservations, active, history, loading, refetch: fetchOnce }
}
